import { useMemo, useState } from "react";
import { useConnections, useDeleteConnection, useNodes, useServices } from "../../api/queries";
import type { Connection, Node } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { QueryStates } from "../../components/QueryStates";
import { ConnectionReachabilityPill, ConnectionTopologyPill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { AddConnectionModal } from "./AddConnectionModal";
import { DeleteConnectionDialog } from "./DeleteConnectionDialog";
import {
  DEFAULT_GATEWAY_PORT,
  useConnectionStatusByConnection,
  useNodeIdsByService,
  type ConnectionRowMeta,
} from "./useConnectionTopology";
import "./services.css";

interface ConnectionsSectionProps {
  projectId: string;
  serviceId: string;
  serviceName: string;
  canManage: boolean;
}

interface DirectionalConnection {
  connection: Connection;
  direction: "outbound" | "inbound";
  otherServiceId: string;
}

/** An address an operator would actually type into a firewall rule —
 * `connectAddress` when the operator has set one (ADR-0029 §5), else the
 * node's own hostname as the best available stand-in (this client has no
 * "tunnel-observed public IP" field to fall back to — see Node's doc
 * comment). */
function dialAddressLabel(node: Node | undefined): string {
  if (!node) return "an unresolved machine";
  return node.connectAddress || node.hostname;
}

/** The firewall guidance for one cross-node row that isn't confirmed
 * reachable (ADR-0029 §5/P3-6): named separately from the row renderer so
 * the "when do we show this" condition reads as one place, not scattered
 * across JSX. Shown for "no" AND "unknown" (including when the live check
 * isn't available at all) — an operator debugging "why can't my service
 * reach the other one" needs this guidance in both cases. */
function shouldShowFirewallHint(meta: ConnectionRowMeta): boolean {
  return meta.topology === "cross-node" && meta.status?.reachable !== "yes";
}

/**
 * This service's edges in the declared topology (ADR-0025 §2) — every
 * connection where it's either endpoint, rendered directionally so an
 * operator reads "what do I call" (→) separately from "what calls me" (←)
 * at a glance. `useConnections` returns the whole project's connections; the
 * filter to "involves this service" happens here, client-side, same as the
 * PR's stated approach (no per-service connections endpoint exists).
 *
 * Each row also carries the ADR-0029 P3-6 cross-node status view: whether
 * the pair is same-node/cross-node/not-yet-placed (computed client-side
 * from the placement graph, always real — see `useNodeIdsByService`) and a
 * live reachability pill (a P3-5 server contract this client is built
 * against but that may not exist yet — the pill degrades to "Unknown"
 * gracefully rather than breaking the row, see `useConnectionStatusByConnection`).
 */
export function ConnectionsSection({ projectId, serviceId, serviceName, canManage }: ConnectionsSectionProps) {
  const { pushToast } = useToast();
  const {
    data: connectionsPage,
    isPending,
    isError,
    error,
    refetch,
  } = useConnections(projectId, { limit: 100 });
  const { data: servicesPage } = useServices(projectId, { limit: 100 });
  const { data: nodesPage } = useNodes({ limit: 100 });
  const deleteMutation = useDeleteConnection(projectId);

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Connection | null>(null);

  const servicesById = useMemo(() => new Map(servicesPage?.items.map((s) => [s.id, s])), [servicesPage]);
  const nodesById = useMemo(() => new Map(nodesPage?.items.map((n) => [n.id, n])), [nodesPage]);
  const candidateServices = useMemo(
    () => (servicesPage?.items ?? []).filter((s) => s.id !== serviceId),
    [servicesPage, serviceId],
  );

  const directional: DirectionalConnection[] = useMemo(() => {
    const all = connectionsPage?.items ?? [];
    return all
      .filter((c) => c.fromService === serviceId || c.toService === serviceId)
      .map((c) =>
        c.fromService === serviceId
          ? { connection: c, direction: "outbound" as const, otherServiceId: c.toService }
          : { connection: c, direction: "inbound" as const, otherServiceId: c.fromService },
      );
  }, [connectionsPage, serviceId]);

  // Covers every possible connection endpoint in this project (this service
  // + every candidate target), not just services already connected — so the
  // "add connection" modal can preview cross-node-ness for a target that
  // has no connection yet.
  const resolvableServiceIds = useMemo(
    () => [serviceId, ...candidateServices.map((s) => s.id)],
    [serviceId, candidateServices],
  );
  const { nodeIdsByService } = useNodeIdsByService(projectId, resolvableServiceIds);

  const connectionsForThisService = useMemo(() => directional.map((d) => d.connection), [directional]);
  const metaByConnectionId = useConnectionStatusByConnection(
    projectId,
    connectionsForThisService,
    nodeIdsByService,
  );

  function serviceLabel(id: string): string {
    return servicesById.get(id)?.name ?? id;
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const connectionId = deleteTarget.id;
    const label = `${serviceLabel(deleteTarget.fromService)} → ${serviceLabel(deleteTarget.toService)}`;
    deleteMutation.mutate(
      { connectionId },
      {
        onSuccess: () => {
          pushToast({ title: `Removed connection ${label}.`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't remove connection ${label}`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const addButton = (
    <Button variant="primary" onClick={() => setAddOpen(true)}>
      Add connection…
    </Button>
  );

  return (
    <section className="service-detail__section" aria-labelledby="service-connections-heading">
      <div className="service-detail__section-header">
        <h2 id="service-connections-heading">Connections</h2>
        {canManage && directional.length > 0 && addButton}
      </div>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading connections…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load connections. Check your connection and try again."
        }
        isEmpty={directional.length === 0}
        empty={
          <EmptyState
            title="No connections yet"
            description={`Declare what "${serviceName}" talks to, or what talks to it, to see it in the topology.`}
            action={canManage ? addButton : undefined}
          />
        }
      >
        <ul className="connections-list">
          {directional.map(({ connection, direction, otherServiceId }) => {
            const meta = metaByConnectionId.get(connection.id);
            const showFirewallHint = meta ? shouldShowFirewallHint(meta) : false;
            const fromNode = meta?.fromNodeIds[0] ? nodesById.get(meta.fromNodeIds[0]) : undefined;
            const toNode = meta?.toNodeIds[0] ? nodesById.get(meta.toNodeIds[0]) : undefined;

            return (
              <li className="connections-list__row" key={connection.id}>
                <div className="connections-list__row-top">
                  <span className="connections-list__label">
                    {direction === "outbound" ? (
                      <>
                        <span aria-hidden="true">→</span> {serviceLabel(otherServiceId)}
                        {connection.port !== null && ` (port ${connection.port}${connection.required ? ", required" : ""})`}
                        {connection.port === null && connection.required && " (required)"}
                        <span className="visually-hidden">, outbound</span>
                      </>
                    ) : (
                      <>
                        <span aria-hidden="true">←</span> {serviceLabel(otherServiceId)}
                        <span className="visually-hidden">, inbound</span>
                      </>
                    )}
                  </span>
                  {canManage && (
                    <Button variant="secondary" onClick={() => setDeleteTarget(connection)}>
                      Delete
                    </Button>
                  )}
                </div>

                {meta && (
                  <div className="connections-list__meta">
                    <ConnectionTopologyPill topology={meta.topology} />
                    {meta.topology === "cross-node" && <ConnectionReachabilityPill reachable={meta.status?.reachable} />}
                  </div>
                )}

                {showFirewallHint && (
                  <p className="connections-list__hint">
                    Cross-machine connections require TCP {DEFAULT_GATEWAY_PORT} open between these machines&rsquo;
                    addresses
                    {fromNode || toNode ? (
                      <>
                        {" "}
                        — {dialAddressLabel(fromNode)} ↔ {dialAddressLabel(toNode)}.
                      </>
                    ) : (
                      "."
                    )}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </QueryStates>

      {canManage && (
        <AddConnectionModal
          open={addOpen}
          projectId={projectId}
          fromServiceId={serviceId}
          fromServiceName={serviceName}
          candidateServices={candidateServices}
          nodeIdsByService={nodeIdsByService}
          onClose={() => setAddOpen(false)}
        />
      )}

      {canManage && (
        <DeleteConnectionDialog
          connection={deleteTarget}
          label={
            deleteTarget ? `${serviceLabel(deleteTarget.fromService)} → ${serviceLabel(deleteTarget.toService)}` : ""
          }
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </section>
  );
}
